/**
 * MonthNavigator — the one month header for every month-scoped page.
 *
 * Phase 22, Task 22.6. Until now each page re-implemented this bar, which is
 * exactly why they drifted: the Billing Report centred the month with the
 * arrows at the extremes, Supplier Invoices squeezed it into the left half,
 * and the Salary Report carried two hand-rolled variants that had to be kept
 * in sync by hand. There is one shape now, and it is this one.
 *
 * THE SHAPE. A full-width bar with the month label CENTRED and previous/next
 * pinned to the EXTREME left and right edges. The three columns are
 * `1fr auto 1fr`, not `justify-between`: two equal side columns are what makes
 * the middle column land on the true centre of the bar. With
 * `justify-between` the month drifts off-centre by half the difference between
 * the two buttons' widths ("Previous" is wider than "Next"), which is the
 * small wrongness the product owner kept seeing.
 *
 * TOUCH TARGETS. Both buttons are at least 44px in both directions. Two of the
 * adopting pages are employee pages used on a phone at the end of a working
 * day (Principle 1), so this is a floor, not a suggestion.
 *
 * ON A PHONE the buttons are the chevron alone, with the word carried by an
 * `aria-label` so a screen reader still hears "Previous"/"Next". This is not
 * decoration: two labelled buttons plus a 24px month heading measure wider
 * than a 360px viewport, and equal-width `1fr` side columns cannot shrink
 * below their content the way the old flex bars did — the bar would overflow
 * sideways instead of squeezing. Below `sm` the side columns are therefore
 * `auto` and the buttons are compact; from `sm` up they are equal `1fr`
 * columns with full labels, which is what makes the month land dead centre on
 * the width where anyone is looking at that.
 *
 * TWO SLOTS, because the adopting pages genuinely need them:
 *
 *   `adornment` — a SMALL control that must stay adjacent to the month label
 *                 itself. LogReports puts its month-lock toggle here; the
 *                 lock is a statement about *that month*, so it has to travel
 *                 with the label rather than sit at an edge. It is rendered
 *                 inside the centre column, so the label plus adornment are
 *                 centred as a group.
 *
 *   `children`  — a full row BELOW the nav row, inside the same bordered
 *                 container. Anything wider than an icon belongs here:
 *                 ReportsPage's employee filter is ~192px and putting it
 *                 beside the label would push the month visibly off centre,
 *                 which is the defect this component exists to remove.
 *
 * The prev/next `data-testid`s live here on purpose. They were duplicated
 * across the two ReportsPage variants (the same id rendered twice, one of
 * them hidden); owning them in one component means exactly one of each can
 * ever exist on a page.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { formatMonthYear } from '../lib/format-utils';
import { cn } from '../lib/utils';

export default function MonthNavigator({
    year,
    month,
    onPrev,
    onNext,
    adornment = null,
    children = null,
    className,
    'data-testid': testId = 'month-navigator',
}) {
    const { t } = useTranslation();

    return (
        <div
            className={cn('border border-border bg-background p-4', className)}
            data-testid={testId}
        >
            <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] sm:grid-cols-[minmax(max-content,1fr)_auto_minmax(max-content,1fr)] items-center gap-2">
                <Button
                    variant="ghost"
                    onClick={onPrev}
                    aria-label={t('common.previous')}
                    className="uppercase tracking-wider text-xs min-h-[44px] min-w-[44px] px-2 sm:px-4 justify-self-start"
                    data-testid="prev-month-btn"
                >
                    <ChevronLeft className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline whitespace-nowrap">{t('common.previous')}</span>
                </Button>

                <div className="flex items-center justify-center gap-2 min-w-0">
                    <h2 className="font-heading text-xl sm:text-2xl font-bold truncate">
                        {formatMonthYear(year, month)}
                    </h2>
                    {adornment}
                </div>

                <Button
                    variant="ghost"
                    onClick={onNext}
                    aria-label={t('common.next')}
                    className="uppercase tracking-wider text-xs min-h-[44px] min-w-[44px] px-2 sm:px-4 justify-self-end"
                    data-testid="next-month-btn"
                >
                    <span className="hidden sm:inline whitespace-nowrap">{t('common.next')}</span>
                    <ChevronRight className="w-4 h-4 sm:ml-1" />
                </Button>
            </div>

            {children && (
                <div className="mt-3 flex justify-center">
                    {children}
                </div>
            )}
        </div>
    );
}
